import type { SeatInfo } from "@app/protocol";
import { useRef } from "react";
import { useT } from "../i18n/context";
import { FACTS, type GameFacts } from "../variants/facts";
import { type AnyView, isTeamGame } from "../variants/types";
import { leaveTable, send } from "../ws/singleton";
import { Avatar } from "./Avatar";
import { CommunityLink } from "./CommunityLink";
import { Icon } from "./icons";
import { useModalFocus } from "./useModalFocus";
import { useRankFrames } from "./useRankFrames";

interface Row {
  key: string;
  label: string;
  score: number;
  member: SeatInfo | null;
  won: boolean;
  /** King: "Out" at zero or above, "Sunk" below (review 0026 S10). */
  note?: "out" | "sunk";
}

/** The seats of a solo game as its match end lists them: Hearts is won from the bottom (0025 U15). */
function finishOrder(rows: Row[], facts: GameFacts): Row[] {
  // King: "Out" at zero or above, "Sunk" below (review 0026 S10).
  const noted = facts.outOrSunk
    ? rows.map((row): Row => ({ ...row, note: row.score >= 0 ? "out" : "sunk" }))
    : rows;
  if (facts.finish === "seated") return noted;
  const up = facts.finish === "lowest" ? 1 : -1;
  return noted.sort((a, b) => (a.score - b.score) * up);
}

/** One card per player, or per team in partnership games. */
function rowsOf(view: AnyView, seats: readonly (SeatInfo | null)[]): Row[] {
  const winners = new Set(view.winners ?? []);
  if (isTeamGame(view))
    return [0, 1].map((team) => ({
      key: `team-${team}`,
      label: [team, team + 2].map((seat) => seats[seat]?.name ?? "?").join(" & "),
      score: view.teamScores[team] ?? 0,
      member: seats[team] ?? null,
      won: winners.has(team) || winners.has(team + 2),
    }));
  const rows = view.scores.map((score, seat) => ({
    key: seats[seat]?.userId ?? `seat-${seat}`,
    label: seats[seat]?.name ?? "?",
    score,
    member: seats[seat] ?? null,
    won: winners.has(seat),
  }));
  return finishOrder(rows, FACTS[view.variant]);
}

/** Match over (design 8): a drawer over the dimmed table, the result, and the way on. */
export function MatchEnd({
  view,
  seats,
  target,
}: {
  view: AnyView;
  seats: readonly (SeatInfo | null)[];
  /** "to 51", as the lobby says it; null when the table plays a number of hands. */
  target: string | null;
}) {
  const { t } = useT();
  const rows = rowsOf(view, seats);
  const rankOf = useRankFrames(view.variant);
  // The match is over and the drawer is all there is: focus comes in and stays (review 0027 G2).
  // No Escape: there is nothing behind it to go back to; "play again" and "leave" are the ways on.
  const drawer = useRef<HTMLDivElement>(null);
  useModalFocus(drawer, true);
  const winners = rows
    .filter((row) => row.won)
    .map((row) => row.label)
    .join(", ");
  return (
    <div
      ref={drawer}
      className="absolute inset-0 z-30"
      role="dialog"
      aria-modal="true"
      aria-labelledby="end-title"
    >
      <div aria-hidden="true" className="absolute inset-0 animate-fade bg-ground/45" />
      <div className="absolute bottom-0 left-1/2 flex w-[min(460px,96%)] -translate-x-1/2 animate-rise flex-col items-center gap-[18px] rounded-t-[24px] border-[1.5px] border-b-0 border-edge bg-panel-strong px-[22px] pb-8 pt-3 shadow-panel backdrop-blur-[16px]">
        <span aria-hidden="true" className="h-[5px] w-11 rounded-full bg-ink/35" />
        <div className="flex flex-col items-center gap-1.5 text-center">
          <h2
            id="end-title"
            className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink/65"
          >
            {t("table.gameEnd.title")}
          </h2>
          <p aria-live="polite" className="text-[24px] font-extrabold">
            {t("table.gameEnd.winner", { names: winners })}
          </p>
          <p className="text-[13px] text-ink/70">
            {target === null
              ? t("home.handsCount", { length: view.hand })
              : t("table.gameEnd.meta", { hands: view.hand, target })}
          </p>
        </div>
        <ol className={`grid w-full gap-2.5 ${rows.length === 2 ? "grid-cols-2" : "grid-cols-4"}`}>
          {rows.map((row) => (
            <li
              key={row.key}
              className={`flex min-w-0 flex-col items-center gap-1.5 rounded-[14px] border-[1.5px] px-1.5 py-2.5 ${row.won ? "border-gold bg-gold/12" : "border-edge bg-ink/5"}`}
            >
              <Avatar
                member={row.member}
                size={rankOf(row.member) === null ? 38 : 52}
                rank={rankOf(row.member)}
              />
              <span className="max-w-full truncate text-[12px] font-semibold">{row.label}</span>
              <span
                className={`text-[15px] font-extrabold tabular-nums ${row.won ? "text-gold" : ""}`}
              >
                {row.score}
              </span>
              {row.note ? (
                <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink/65">
                  {t(`king.end.${row.note}`)}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
        <div className="grid w-full grid-cols-2 gap-3">
          <button
            type="button"
            className="btn-glass flex h-[52px] items-center justify-center gap-2 rounded-pill text-[15px] font-bold"
            onClick={leaveTable}
          >
            <Icon name="exit" size={18} strokeWidth={2.4} />
            {t("table.leave")}
          </button>
          <button
            type="button"
            className="btn-gold flex h-[52px] items-center justify-center gap-2 rounded-pill text-[15px] font-extrabold"
            onClick={() => send({ type: "again" })}
          >
            <Icon name="refresh" size={18} strokeWidth={2.6} />
            {t("table.again")}
          </button>
        </div>
        <CommunityLink className="text-center" />
      </div>
    </div>
  );
}
